import { useState, useRef, useEffect } from "react";
import "./css/auth.css";
import logo from "../../assets/images/logo1.png";
import CustomSvg from "../svgs/CustomSvg";
import { useNavigate } from "react-router-dom";
import { verifyOTP } from "../apiRequests/requestApi";
import { useUser } from "../../utils/hooks";
import { Spinner } from "react-bootstrap";
import CustomErrorMsg from "../errorMsg/CustomErrorMsg";
import ScrollTo from "../scroll/ScrollTo";
import helpers from "../../components/helpers/helpers";
import { getRequest, postRequest } from "../apiRequests/requestApi";


export default function EmailVerification({ type, accountType }) {
  const { user, setCode } = useUser();
  const { apiUrl } = helpers;

  const navigate = useNavigate();
  const navigateTo = (path) => navigate(path);
  const goToLogin = () => navigateTo("/login");
  const goToResetPassword = () => navigateTo("/forgot-password/reset-password");

  const [otp, setOtp] = useState(new Array(6).fill(""));
  const inputRefs = useRef([]);
  const [apiReqs, setApiReqs] = useState({
    isLoading: false,
    data: null,
    errorMsg: null,
  });

  const handleChange = (element, index) => {
    if (isNaN(element.value)) return;

    setOtp([...otp.map((d, idx) => (idx === index ? element.value : d))]);

    // Move to the next input field if the current one is filled
    if (element.nextSibling && element.value) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && inputRefs.current[index - 1]) {
      inputRefs.current[index - 1].focus();
    }
  };

  const [countdown, setCountdown] = useState(60);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setInterval(() => {
        setCountdown((prevCountdown) => prevCountdown - 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [countdown]);

  useEffect(() => {
    if (apiReqs.data && apiReqs.isLoading) {
      const { data } = apiReqs;
      submitOtp({ requestBody: data });
    }
  }, [apiReqs]);

  const onBtnClick = () => {
    const code = otp.join("");
    setCode(code);
    setApiReqs({ isLoading: true, data: { otp: code }, errorMsg: null });

    submitOtp({ requestBody: { otp: code } });
  };
  const submitOtp = async ({ requestBody }) => {
    const details = {
      email: user?.email,
      ...requestBody,
    };

    try {
      if (type === "resetPassword") {
        localStorage.setItem("resetPasswordToken", details.otp);
        return goToResetPassword();
      }

      if (type === "activateAcct") {
        const response = await verifyOTP(details);
        setApiReqs({ isLoading: false, data: null, errorMsg: null });
        return goToLogin();
      }
    } catch (error) {
      setApiReqs({
        isLoading: false,
        data: null,
        errorMsg: error.message ? "OTP is incorrect" : "Network Error",
      });

      // console.error(error);
    }
  };

  const resendOtp = async () => {
    setApiReqs({ ...apiReqs, isLoading: true, errorMsg: null });
    try {
      const response = await postRequest({
        url: "/auth/resend-verify-email-otp",
        data: { email: user?.email },
      });
      if (!response.success) throw new Error(response.message || "Failed to resend OTP");
      setCountdown(60);
      setApiReqs({ ...apiReqs, isLoading: false, errorMsg: null });
    } catch (error) {
      setApiReqs({ ...apiReqs, isLoading: false, errorMsg: error.message || "Failed to resend OTP" });
    }
  };

  return (
    <ScrollTo condition={apiReqs.errorMsg}>
      <div className="bg-[#130828] flex pt-20  m-auto px-3 md:px-5 lg:px-5 min-h-screen">
        <div className="flex flex-col items-center w-full">
          <div className="w-full max-w-lg flex flex-col items-center">
            <h1 className="text-white font-quantico font-bold text-xl md:text-3xl mb-4 text-center">
              {type === "resetPassword" ? (
                <>
                  <span
                    className={`bg-clip-text text-transparent ${
                      accountType === "celebrity"
                        ? "bg-gradient-to-r from-[#56920D] to-[#B7D080]"
                        : "bg-gradient-to-r from-[#BD3193] to-[#9FCB3A]"
                    }`}
                  >
                    You’ve got an email
                  </span>
                </>
              ) : (
                <>
                  Verify{" "}
                  <span
                    className={`bg-clip-text text-transparent ${
                      accountType === "celebrity"
                        ? "bg-gradient-to-r from-[#56920D] to-[#B7D080]"
                        : "bg-gradient-to-r from-[#BD3193] to-[#9FCB3A]"
                    }`}
                  >
                    Email
                  </span>
                </>
              )}
            </h1>

            <p className="text-white/70 text-xs font-poppins font-light mb-5 text-center">
              We’ve sent the OTP verification code to your email address.<br/> Check
              your email and enter the code below.
            </p>

            {apiReqs.errorMsg && (
              <CustomErrorMsg errorMsg={apiReqs.errorMsg} verticalPadding />
            )}

            <div className="flex justify-between items-center mb-4 pb-3 gap-2">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  name="otp"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleChange(e.target, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  ref={(el) => (inputRefs.current[index] = el)}
                  className="w-8 h-8 text-center text-lg font-semibold text-white bg-gray-800 border border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                  onPaste={(e) => {
                    const paste = e.clipboardData.getData('text').replace(/\D/g, '');
                    if (paste.length === 6) {
                      setOtp(paste.split('').slice(0, 6));
                      setTimeout(() => {
                        inputRefs.current[5]?.focus();
                      }, 0);
                      e.preventDefault();
                    }
                  }}
                />
              ))}
            </div>

            <button
              onClick={onBtnClick}
              disabled={apiReqs.isLoading}
              style={{ opacity: apiReqs.isLoading ? 0.5 : 1 }}
              className={`w-1/2 p-2 rounded flex items-center justify-center transition duration-200 shadow-lg ${
                accountType === "celebrity"
                  ? "bg-gradient-to-r from-[#56920D] to-[#B7D080] hover:from-[#407a0a] hover:to-[#8fcf2a]"
                  : "bg-gradient-to-r from-[#BD3193] to-[#9FCB3A] hover:from-[#a11b7a] hover:to-[#7bbd2a]"
              } text-white font-bold text-lg`}
            >
              {apiReqs.isLoading ? (
                <Spinner variant="light" size="sm" />
              ) : (
                <>
                  <p className="text-sm font-medium font-poppins mx-1">
                    Continue
                  </p>
                  <div className="mx-2 flex items-center">
                    <CustomSvg name="arrow-right" />
                  </div>
                </>
              )}
            </button>

            <div className="mt-6 mb-2 flex flex-col items-center">
              <div className="flex items-center gap-2">
                <span className="text-white/80 text-sm font-poppins font-light">
                  Didn’t receive the email?
                </span>
                <span className="animate-bounce text-yellow-400 font-semibold ml-1">
                  <CustomSvg name="mail" size={18} />
                </span>
              </div>
              <span className="text-yellow-400 text-xs mt-1 italic">
                Also check your spam or junk folder.
              </span>
              <div className="mt-2 text-white/80 text-xs font-poppins">
                You can resend the code in{" "}
                <span
                  className={
                    accountType === "celebrity"
                      ? "text-[#56920D] font-bold"
                      : "text-pink-600 font-bold"
                  }
                >
                  {countdown > 0 ? `${countdown}s` : (
                    <button
                      onClick={resendOtp}
                      className="underline text-xs font-bold"
                      disabled={apiReqs.isLoading}
                    >
                      Resend OTP
                    </button>
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ScrollTo>
  );
}
